import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

/**
 * 배포 직후의 청크 로딩 실패를 스스로 복구하는 `React.lazy` 래퍼.
 *
 * 왜 필요한가
 * ----------
 * 화면은 전부 코드 분할되어 `assets/page-<해시>.js` 로 나뉜다. 해시는 빌드마다 바뀌고
 * 배포는 예전 파일을 지운다. 그래서 **탭을 열어 둔 채 새 배포가 나가면** 그 탭이 들고 있는
 * 예전 `index.html` 이 이미 사라진 청크를 가리키게 되고, 그 화면으로 이동하는 순간
 * `Failed to fetch dynamically imported module` 이 난다. 새로고침하면 새 `index.html` 을
 * 받아 오므로 멀쩡해지는 것도 같은 이유다.
 *
 * 근본 대책은 서버가 예전 청크를 일정 기간 남겨 두는 것이고(`.github/workflows/deploy-frontend.yml`),
 * 이 래퍼는 그래도 새는 경우(보관 기간이 지난 아주 오래된 탭 등)를 위한 두 번째 그물이다.
 *
 * 무한 새로고침을 막는 법
 * ----------------------
 * 새로고침 직후에 또 실패하면 배포 문제가 아니라 진짜 오류(네트워크 단절 등)다.
 * 마지막 자동 새로고침 시각을 `sessionStorage` 에 남겨 두고, 그 직후의 실패는
 * 그대로 위로 던져 오류 화면(`RouteError`)이 뜨게 한다.
 */

const RELOAD_AT_KEY = "hj:chunk-reload-at";

/** 이 시간(ms) 안에 또 실패하면 새 배포 탓이 아니라고 본다 */
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * 청크를 못 받아 온 오류인지 판정한다.
 *
 * 브라우저마다 문구가 다르다.
 * - Chrome  `Failed to fetch dynamically imported module: ...`
 * - Safari  `Importing a module script failed.`
 * - Firefox `error loading dynamically imported module`
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  return /dynamically imported module|module script failed|Failed to fetch|ChunkLoadError/i.test(
    message
  );
}

function reloadedJustNow(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);
    return at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // 사파리 사생활 보호 모드 등에서 sessionStorage 가 막히면 자동 복구를 포기한다.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    /* 저장에 실패해도 새로고침 자체는 시도한다 */
  }
}

export function lazyRoute<T extends ComponentType<unknown>>(
  load: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await load();
    } catch (error) {
      if (!isChunkLoadError(error) || reloadedJustNow()) throw error;
      markReloaded();
      window.location.reload();
      // 새로고침이 끝날 때까지 Suspense 를 붙잡아 둔다. 여기서 무언가를 돌려주면
      // 사라지는 화면이 한 번 깜빡인다.
      return await new Promise<never>(() => {});
    }
  });
}
